/**
 * @module NotificationsPage
 * @package @geenius/notifications-react-css
 * @description Renders the full inbox page for the React vanilla CSS variant.
 */

import { FadeIn } from '@geenius/motion/react-css'
import type {
  Notification,
  NotificationStatus,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  groupNotificationsForDisplay,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/react-css'
import type { JSX } from 'react'
import { useMemo } from 'react'
import { EmptyNotifications } from '../components/EmptyNotifications'
import { NotificationItem } from '../components/NotificationItem'
import { useNotifications } from '../hooks/useNotifications'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for the React CSS notifications page.
 */
export interface NotificationsPageProps {
  notifications: Notification[] | undefined
  onMarkRead?: (id: string) => Promise<void>
  onMarkAllRead?: () => Promise<void>
  onDismiss?: (id: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  className?: string
}

const LOADING_ROW_KEYS = [
  'notifications-page-row-1',
  'notifications-page-row-2',
  'notifications-page-row-3',
  'notifications-page-row-4',
  'notifications-page-row-5',
  'notifications-page-row-6',
]

const NOTIFICATION_STATUS_FILTERS: ReadonlyArray<NotificationStatus | 'all'> = [
  'all',
  'unread',
  'read',
]

/**
 * Renders the full notifications inbox.
 *
 * @param props Page props including notification data and mutation handlers.
 * @returns The notifications page markup.
 */
export function NotificationsPage({
  notifications: rawNotifications,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onDelete,
  className,
}: NotificationsPageProps): JSX.Element {
  const { plural, t } = useNotificationCopy()
  const {
    notifications,
    unreadCount,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    isLoading,
  } = useNotifications(rawNotifications, {
    markRead: onMarkRead ?? (async () => {}),
    markAllRead: onMarkAllRead ?? (async () => {}),
    dismiss: onDismiss ?? (async () => {}),
    deleteNotification: onDelete ?? (async () => {}),
  })

  const groupedSections = useMemo(
    () => Array.from(groupNotificationsForDisplay(notifications, t).entries()),
    [notifications, t],
  )

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.notificationsTitle'),
    description: t('notifications.seo.notificationsDescription'),
    path: '/notifications',
    keywords: ['notifications', 'inbox', 'alerts', 'activity'],
  })

  if (isLoading) {
    return (
      <div className={cx('notifications__skeleton-list', className)}>
        {LOADING_ROW_KEYS.map((key) => (
          <div
            key={key}
            className={cx('notifications__skeleton', 'notifications__skeleton--item')}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <SEOHead meta={seoMeta} />
      <div className={cx('notifications__page', className)}>
        <FadeIn className={cx('notifications__page-header')}>
          <div>
            <h1 className={cx('notifications__page-title')}>
              {t('notifications.common.title')}
            </h1>
            <p className={cx('notifications__page-subtitle')}>
              {plural('notifications.common.unreadSummary', unreadCount)}
            </p>
          </div>
          {unreadCount > 0 ? (
            <button
              type="button"
              className={cx('notifications__btn', 'notifications__btn--primary')}
              onClick={onMarkAllRead}
            >
              {t('notifications.common.markAllRead')}
            </button>
          ) : null}
        </FadeIn>
        <FadeIn delay={60} className={cx('notifications__filter-tabs')}>
          {NOTIFICATION_STATUS_FILTERS.map((status) => (
            <button
              key={status}
              type="button"
              aria-pressed={statusFilter === status}
              className={cx(
                'notifications__filter-tab',
                statusFilter === status
                  ? 'notifications__filter-tab--active'
                  : 'notifications__filter-tab--inactive',
              )}
              onClick={() => setStatusFilter(status)}
            >
              {getNotificationStatusLabel(status, t)}
            </button>
          ))}
        </FadeIn>
        <FadeIn delay={120} className={cx('notifications__type-tabs')}>
          <button
            type="button"
            aria-pressed={typeFilter === 'all'}
            className={cx(
              'notifications__type-tab',
              typeFilter === 'all' && 'notifications__type-tab--active',
            )}
            onClick={() => setTypeFilter('all')}
          >
            {t('notifications.filters.typeAll')}
          </button>
          {NOTIFICATION_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              aria-pressed={typeFilter === type}
              className={cx(
                'notifications__type-tab',
                `notifications__type-tab--${type}`,
                typeFilter === type && 'notifications__type-tab--active',
              )}
              onClick={() => setTypeFilter(type)}
            >
              {TYPE_CONFIG[type].icon} {getNotificationTypeLabel(type, t)}
            </button>
          ))}
        </FadeIn>
        {notifications.length === 0 ? (
          <EmptyNotifications />
        ) : (
          groupedSections.map(([label, items], index) => (
            <FadeIn key={label} delay={160 + index * 40}>
              <div className={cx('notifications__date-group')}>
                <h2 className={cx('notifications__date-label')}>{label}</h2>
                <div className={cx('notifications__date-list')}>
                  {items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onRead={onMarkRead}
                      onDismiss={onDismiss}
                    />
                  ))}
                </div>
              </div>
            </FadeIn>
          ))
        )}
      </div>
    </>
  )
}
